/**
 * Particle presets — counts, colours and emission shapes VERBATIM from the
 * reference prototype (docs/briefs/REFERENCE-blg-matchday-prototype-v2.html
 * ~L566-579), plus the Pack-H `ring` preset the prototype does not carry.
 *
 * Colours come from the extracted comp tokens (packages/ui/tokens):
 *   volt 200,240,49 · gold 255,194,75 · text 244,246,248.
 */

import type { ParticleInit } from "./engine.js";

export const PARTICLE_COLORS = {
  volt: "200,240,49",
  gold: "255,194,75",
  white: "244,246,248",
  dust: "170,158,132",
  ember: "255,170,60",
} as const;

export type Rng = () => number;

export interface PresetSpec {
  count: number;
  factory: (i: number) => ParticleInit;
}

/** Dust puff under the crate drop — 16 particles. */
export function puff(w: number, h: number, rng: Rng = Math.random): PresetSpec {
  const cx = w / 2;
  const cy = h * 0.6;
  return {
    count: 16,
    factory: () => ({
      x: cx + (rng() * 100 - 50),
      y: cy,
      vx: (rng() - 0.5) * 2,
      vy: -rng() * 1.6,
      life: 1,
      dec: 0.03,
      size: 3 + rng() * 3,
      col: PARTICLE_COLORS.dust,
      sh: "dot",
    }),
  };
}

/** The 60-particle ceremony burst — 62% gold / 38% volt, 32% streaks. */
export function burst(w: number, h: number, rng: Rng = Math.random): PresetSpec {
  const cx = w / 2;
  const cy = h * 0.48;
  return {
    count: 60,
    factory: () => {
      const a = rng() * Math.PI * 2;
      const v = 2.2 + rng() * 5.5;
      return {
        x: cx,
        y: cy,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v - 2.2,
        life: 1,
        dec: 0.015,
        size: 2 + rng() * 4.5,
        col: rng() < 0.62 ? PARTICLE_COLORS.gold : PARTICLE_COLORS.volt,
        sh: rng() < 0.32 ? "streak" : "dot",
      };
    },
  };
}

/** Confetti — 46 falling rects, volt/gold/white, rotating. */
export function confetti(w: number, rng: Rng = Math.random): PresetSpec {
  const cols = [PARTICLE_COLORS.volt, PARTICLE_COLORS.gold, PARTICLE_COLORS.white];
  return {
    count: 46,
    factory: () => ({
      x: rng() * w,
      y: -12,
      vx: (rng() - 0.5) * 1.6,
      vy: 1.6 + rng() * 2.6,
      life: 1,
      dec: 0.006,
      size: 4 + rng() * 4,
      rot: rng() * 6,
      vr: (rng() - 0.5) * 0.26,
      col: cols[Math.floor(rng() * 3)] ?? PARTICLE_COLORS.volt,
      sh: "rect",
    }),
  };
}

/** One rising ember — the ambient spawner emits this every EMBER_INTERVAL_MS. */
export const EMBER_INTERVAL_MS = 170;

export function ember(w: number, h: number, rng: Rng = Math.random): PresetSpec {
  const fx = w / 2;
  return {
    count: 1,
    factory: () => ({
      x: fx + (rng() * 70 - 35),
      y: h * 0.55,
      vx: (rng() - 0.5) * 0.6,
      vy: -(0.8 + rng() * 1.2),
      life: 1,
      dec: 0.012,
      size: 2 + rng() * 2,
      col: rng() < 0.5 ? PARTICLE_COLORS.ember : PARTICLE_COLORS.volt,
      sh: "dot",
      rise: true,
    }),
  };
}

/** Pack-H ring — 36 dots emitted radially on an expanding circle. */
export function ring(
  cx: number,
  cy: number,
  rng: Rng = Math.random,
  col: string = PARTICLE_COLORS.volt,
): PresetSpec {
  const n = 36;
  return {
    count: n,
    factory: (i) => {
      const a = (i / n) * Math.PI * 2;
      const v = 3 + rng() * 1.2;
      return {
        x: cx + Math.cos(a) * 8,
        y: cy + Math.sin(a) * 8,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v - 0.6,
        life: 1,
        dec: 0.02,
        size: 2 + rng() * 2,
        col,
        sh: "dot",
      };
    },
  };
}

export type ParticlePresetName = "burst" | "confetti" | "puff" | "embers" | "ring";
